import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia'
import { useBackendStore } from './backend'
import { computed } from 'vue'
import { useUserStore } from './user'
import { useSettingStore } from '../modules/setting'
import storeConfigs from 'constants/storeConfigs.ts'

export const useClientStore = defineStore(storeConfigs.client.name, () => {
	const { url } = storeToRefs(useBackendStore())
	const { whoami } = storeToRefs(useUserStore())
	const { data } = storeToRefs(useSettingStore())

	const database = computed(() =>
		whoami.value?.clientdatabases.find(
			(database) => database.subdomain === url.value?.subdomain
		)
	)

	const isHpDomain = computed(() => url.value?.domain?.includes('hausperfekt'))
	const isHp = computed(
		() =>
			erp.value === 'hpGen' || erp.value === 'hpXpa' || erp.value === 'hpPsql'
	)

	const settings = computed(() => data.value)
	const erp = computed(() => database.value?.erp)

	// True when the backend flags this tenant for the legacy-sync installer
	// update (AAR-1335). Drives the LegacySyncUpdateBanner.
	const syncUpdatePending = computed(
		() => data.value?.sync_update_pending ?? false
	)

	const isKarthago = computed(() => erp.value === 'karth')
	const isPsql = computed(() => erp.value === 'hpPsql')
	const isPowerHaus = computed(() => erp.value === 'power')

	// AAR-1494 — das Modul Persoenliche Buchungskonten ist fachlich nur fuer
	// Karthago- und hpPsql-Mandanten verfuegbar.
	//
	// VORUEBERGEHEND KOMPLETT DEAKTIVIERT (2026-08-17).
	// Grund: unklar, ob die Datengrundlage ueberhaupt bei Kunden ankommt. Die
	// Sync-Seite kennt die Entitaeten zwar (AccountPerson, mobile_account_persons
	// als Matview inkl. Refresh-Migration), aber in keiner der lokalen
	// Mandanten-DBs existiert eine Tabelle account_person — auch nicht beim
	// Karthago-Mandanten. Solange nicht belegt ist, dass die Daten real
	// ankommen, zeigt das Modul Nutzern leere Ansichten.
	//
	// Dieser eine Schalter deckt ALLE Einstiegspunkte ab: Profil-Button
	// "Meine Konten", Personen-Konten-Tab und Route myAccounts (ueber
	// usePersonalAccountsAccess) sowie die Admin-Flaechen in
	// Settings/Generic.vue und Management/Roles/Role.vue, die direkt hierauf
	// zugreifen.
	//
	// Zum Reaktivieren: die Konstante entfernen und die urspruengliche Zeile
	// wieder aktiv setzen (darunter auskommentiert erhalten).
	const PERSONAL_ACCOUNTS_ENABLED = false
	const canPersonalAccounts = computed(
		() => PERSONAL_ACCOUNTS_ENABLED && (isKarthago.value || isPsql.value)
	)
	// Ursprung: const canPersonalAccounts = computed(() => isKarthago.value || isPsql.value)

	const subscriptions = computed(() => data.value?.subscriptions)
	const subscriptionPerks = computed(
		() =>
			subscriptions.value
				?.flatMap((subscription) => [
					...subscription.subscriptionModules,
					...subscription.subscriptionMethods,
				])
				?.map((item) => item.name) ?? []
	)

	return {
		database,
		settings,
		subscriptions,
		subscriptionPerks,
		erp,
		isKarthago,
		isPsql,
		canPersonalAccounts,
		isHp,
		isHpDomain,
		syncUpdatePending,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useClientStore, import.meta.hot))
}
